import { useState, useEffect } from 'react';
import {
  Tag as TagIcon,
  Plus,
  X,
  Edit2,
  Trash2,
  Save,
  Check,
  Loader2,
} from 'lucide-react';
import BaseModal from '@/shared/components/modals/core/BaseModal';
import Button from '@/shared/components/elements/Button';
import Select from '@/shared/components/elements/Select';
import useTag from '@/shared/hooks/useTag';

export default function TagModal({ isOpen, onClose, entity, entityType }) {
  const [searchQuery, setSearchQuery] = useState('');
  const [editingTagId, setEditingTagId] = useState(null);
  const [editTagValue, setEditTagValue] = useState('');

  const [localTags, setLocalTags] = useState([]);
  const [removingTagId, setRemovingTagId] = useState(null);

  const {
    tagList,
    createTag,
    updateTag,
    deleteTag,
    isCreating,
    isUpdating,
    isDeleting,
    assignDevice,
    isAssigningDevice,
    assignGateway,
    isAssigningGateway,
    removeDeviceFromTag,
    removeGatewayFromTag,
    isLoadingList,
  } = useTag(null, 1, 50, { name: searchQuery });

  useEffect(() => {
    setLocalTags(entity?.tags || []);
  }, [entity]);

  const isAssigning =
    entityType === 'gateway' ? isAssigningGateway : isAssigningDevice;
  const isLoadingAction = isLoadingList || isCreating || isAssigning;

  const handleAssignEntity = async (tagId, tagObject) => {
    if (!entity) return;
    if (tagObject?.system_key) return;
    if (localTags.some((t) => t.id === tagId)) return;

    if (entityType === 'gateway') {
      await assignGateway({ id: tagId, gatewayIds: [entity.id] });
    } else if (entityType === 'device') {
      await assignDevice({ id: tagId, deviceIds: [entity.id] });
    }

    if (tagObject) {
      setLocalTags((curr) =>
        curr.some((t) => t.id === tagId) ? curr : [...curr, tagObject],
      );
    }
  };

  const handleSelectChange = async (selectedValue) => {
    if (!selectedValue || !entity) return;

    if (selectedValue === 'CREATE_NEW_TAG') {
      if (!searchQuery.trim()) return;
      try {
        const response = await createTag({ name: searchQuery });
        const newTag = response?.data || response;
        const newTagId = newTag?.id;
        if (newTagId) {
          await handleAssignEntity(newTagId, {
            id: newTagId,
            name: newTag.name || searchQuery,
          });
          setSearchQuery('');
        }
      } catch (error) {
        console.error('Create and assign tag error:', error);
      }
    } else {
      try {
        const selectedOption = tagOptions.find(
          (o) => o.value === selectedValue,
        );
        await handleAssignEntity(selectedValue, selectedOption?.original);
      } catch (error) {
        console.error('Assign tag error:', error);
      }
    }
  };

  const handleRemoveTag = async (tag) => {
    if (removingTagId || !entity || tag.system_key) return;

    const previousTags = localTags;
    setRemovingTagId(tag.id);
    setLocalTags((curr) => curr.filter((t) => t.id !== tag.id));

    try {
      if (entityType === 'gateway') {
        await removeGatewayFromTag({ id: tag.id, gatewayTagId: entity.id });
      } else {
        await removeDeviceFromTag({ id: tag.id, deviceTagId: entity.id });
      }
    } catch (error) {
      console.error('Remove tag error:', error);
      setLocalTags(previousTags);
    } finally {
      setRemovingTagId(null);
    }
  };

  const startEditTag = (e, tag) => {
    e.stopPropagation();
    setEditingTagId(tag.id);
    setEditTagValue(tag.name);
  };

  const cancelEditTag = (e) => {
    e.stopPropagation();
    setEditingTagId(null);
    setEditTagValue('');
  };

  const saveEditTag = async (e, tagId) => {
    e.stopPropagation();
    if (!editTagValue.trim() || isUpdating) return;
    try {
      await updateTag({ id: tagId, name: editTagValue });
      setLocalTags((curr) =>
        curr.map((t) => (t.id === tagId ? { ...t, name: editTagValue } : t)),
      );
      setEditingTagId(null);
      setEditTagValue('');
    } catch (error) {
      console.error('Update tag error:', error);
    }
  };

  const handleDeleteTagItem = async (e, tagId) => {
    e.stopPropagation();
    if (isDeleting) return;
    try {
      await deleteTag(tagId);
      setLocalTags((curr) => curr.filter((t) => t.id !== tagId));
    } catch (error) {
      console.error('Delete tag error:', error);
    }
  };

  // --- Prepare Tag Options ---
  const tagOptions =
    tagList?.items?.map((tag) => ({
      value: tag.id,
      label: tag.name,
      disabled: !!tag.system_key,
      original: tag,
    })) || [];

  const exactMatchExists = tagOptions.some((opt) => opt.label === searchQuery);

  if (searchQuery && !exactMatchExists) {
    tagOptions.push({
      value: 'CREATE_NEW_TAG',
      label: `ایجاد تگ "${searchQuery}"`,
      isCreate: true,
    });
  }

  // --- Custom Render Option ---
  const renderCustomOption = (option) => {
    if (option.isCreate) {
      return (
        <div className="text-primary-100 flex w-full items-center gap-2 dark:text-orange-400">
          <Plus size={16} />
          <span className="font-medium">{option.label}</span>
        </div>
      );
    }

    const isEditing = editingTagId === option.value;

    if (isEditing) {
      return (
        <div
          className="flex w-full items-center gap-2"
          onClick={(e) => e.stopPropagation()}
        >
          <input
            autoFocus
            type="text"
            value={editTagValue}
            onChange={(e) => setEditTagValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') saveEditTag(e, option.value);
              if (e.key === 'Escape') cancelEditTag(e);
            }}
            className="focus:border-primary-100 flex-1 rounded border border-gray-300 px-2 py-1 text-sm outline-none dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          />
          <button
            onClick={(e) => saveEditTag(e, option.value)}
            disabled={isUpdating}
            className="text-success-100 hover:text-success-120 dark:text-green-400"
          >
            <Save size={16} />
          </button>
          <button
            onClick={cancelEditTag}
            className="text-tertiary-60 hover:text-tertiary-100 dark:text-gray-400"
          >
            <X size={16} />
          </button>
        </div>
      );
    }

    const isAssigned = localTags.some((tag) => tag.id === option.value);

    return (
      <div className="group flex w-full items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={isAssigned ? 'font-medium' : ''}>
            {option.label}
          </span>
          {isAssigned && (
            <Check
              size={16}
              className="text-primary-100 dark:text-primary-400"
            />
          )}
        </div>
        <div className="flex items-center gap-2 opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100">
          <button
            onClick={(e) => startEditTag(e, option.original)}
            disabled={!!option.original?.system_key}
            className="p-1 text-blue-500 hover:text-blue-600 dark:text-blue-400"
            title="ویرایش"
          >
            <Edit2 size={14} />
          </button>
          <button
            onClick={(e) => handleDeleteTagItem(e, option.value)}
            disabled={isDeleting || !!option.original?.system_key}
            className="text-error-100 hover:text-error-120 p-1 dark:text-red-400"
            title="حذف"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    );
  };

  const getEntityTitleName = () =>
    entityType === 'gateway' ? 'هاب' : 'دستگاه';

  return (
    <BaseModal
      isOpen={isOpen}
      onClose={onClose}
      title={`تگ‌های ${entity?.name || getEntityTitleName()}`}
      icon={<TagIcon size={20} />}
      iconVariant="primary"
      size="md"
      closeOnBackdropClick={!isLoadingAction}
      closeOnEscape={!isLoadingAction}
      showCloseButton={!isLoadingAction}
      footer={
        <Button
          type="button"
          variant="secondary"
          size="lg"
          fullWidth
          onClick={onClose}
          disabled={isLoadingAction}
        >
          بستن
        </Button>
      }
    >
      <div className="space-y-4 p-4 sm:space-y-5 sm:p-6">
        {localTags.length > 0 && (
          <div className="space-y-2.5">
            <div className="text-tertiary-80 flex items-center gap-1.5 text-sm font-medium dark:text-gray-300">
              <TagIcon size={14} className="opacity-70" />
              <span>تگ‌های انتخاب‌شده</span>
              <span className="bg-primary-10 text-primary-100 inline-flex h-5 min-w-5 items-center justify-center rounded-full px-1.5 text-[11px] font-bold dark:bg-orange-500/15 dark:text-orange-300">
                {localTags.length}
              </span>
            </div>

            <div className="border-tertiary-10 flex flex-wrap gap-2 rounded-xl border border-dashed p-2.5 dark:border-gray-700/60">
              {localTags.map((tag) => {
                const isRemoving = removingTagId === tag.id;
                return (
                  <span
                    key={tag.id}
                    className={`border-primary-20 bg-primary-10 text-primary-100 inline-flex items-center gap-1.5 rounded-full border py-1 pr-3 pl-1.5 text-sm font-medium transition-all duration-200 dark:border-orange-500/30 dark:bg-orange-500/15 dark:text-orange-300 ${
                      isRemoving
                        ? 'opacity-60'
                        : 'hover:border-primary-100/40 hover:shadow-sm'
                    }`}
                  >
                    <TagIcon size={12} className="opacity-70" />
                    <span className="max-w-[140px] truncate leading-none">
                      {tag.name}
                    </span>
                    <button
                      type="button"
                      onClick={() => handleRemoveTag(tag)}
                      disabled={isRemoving || !!tag.system_key}
                      aria-label={`حذف تگ ${tag.name}`}
                      title={
                        tag.system_key
                          ? 'عضویت خودکار؛ قابل حذف نیست'
                          : 'حذف تگ'
                      }
                      className="text-primary-100/60 hover:bg-error-100/10 hover:text-error-100 flex h-5 w-5 items-center justify-center rounded-full transition-colors disabled:cursor-not-allowed dark:text-orange-300/60 dark:hover:bg-red-500/15 dark:hover:text-red-400"
                    >
                      {isRemoving ? (
                        <Loader2 size={13} className="animate-spin" />
                      ) : (
                        <X size={13} />
                      )}
                    </button>
                  </span>
                );
              })}
            </div>
          </div>
        )}

        <Select
          showSearch
          closeOnSelect={false}
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          options={tagOptions}
          value=""
          onChange={handleSelectChange}
          placeholder="انتخاب یا جستجوی تگ..."
          loading={isLoadingAction}
          emptyMessage="تگی یافت نشد. برای ایجاد تایپ کنید."
          renderOption={renderCustomOption}
          className="w-full"
        />
      </div>
    </BaseModal>
  );
}
